import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, Mail, MessageCircle, ArrowRight, CheckCircle, Clock } from "lucide-react";
import { FORMS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/sneha-logo.jpg";

const ContactForm = ({ title, leadType, fields }) => {
  const [form, setForm] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: leadType });
      setStatus("success");
      setForm({});
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  if (status === "success") return (
    <div className="glass-gold p-8 text-center">
      <CheckCircle size={36} className="text-[#D4AF37] mx-auto mb-4" />
      <h3 className="font-display text-xl text-[#FAF8F3] mb-2">Request Received</h3>
      <p className="text-[#666] text-sm">Our team will contact you within 24 hours.</p>
      <button onClick={() => setStatus(null)} className="btn-gold text-xs py-2 px-5 mt-4">Submit Another</button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="glass-gold p-8 space-y-5">
      <h3 className="font-display text-xl text-[#FAF8F3] mb-2">{title}</h3>
      {fields.map((f) => (
        <div key={f.key}>
          <label className="text-xs text-[#A0A0A0] tracking-wider uppercase mb-2 block">{f.label}</label>
          {f.type === "select" ? (
            <select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="input-luxury bg-transparent" required={f.required}>
              <option value="" className="bg-[#111]">Select {f.label}</option>
              {f.options.map((o) => <option key={o} value={o} className="bg-[#111]">{o}</option>)}
            </select>
          ) : f.type === "textarea" ? (
            <textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="input-luxury resize-none h-20" placeholder={f.placeholder} rows={3} />
          ) : (
            <input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              type={f.type || "text"} className="input-luxury" placeholder={f.placeholder}
              required={f.required} />
          )}
        </div>
      ))}
      <button type="submit" disabled={loading} data-testid={FORMS.submitBtn}
        className="btn-gold-solid btn-gold w-full justify-center py-3 text-xs">
        {loading ? "Sending..." : "Submit Request"}
        {!loading && <ArrowRight size={12} className="inline ml-2" />}
      </button>
      {status === "error" && <p className="text-red-400 text-xs text-center">Something went wrong. Please call us directly.</p>}
    </form>
  );
};

const Contact = () => {
  const [activeForm, setActiveForm] = useState("contact");

  const formConfigs = {
    contact: {
      title: "General Enquiry",
      leadType: "contact",
      fields: [
        { key: "name", label: "Full Name", placeholder: "Your full name", required: true },
        { key: "phone", label: "Phone", placeholder: "+91 XXXXX XXXXX", required: true },
        { key: "email", label: "Email", type: "email", placeholder: "your@email.com" },
        { key: "message", label: "Message", type: "textarea", placeholder: "How can we help you?" },
      ]
    },
    site_visit: {
      title: "Book Site Visit",
      leadType: "site_visit",
      fields: [
        { key: "name", label: "Full Name", placeholder: "Your full name", required: true },
        { key: "phone", label: "Phone", placeholder: "+91 XXXXX XXXXX", required: true },
        { key: "location", label: "Preferred Location", type: "select", options: ["Kharghar", "Panvel", "Seawoods", "Ulwe", "Nerul", "Belapur", "Other"], required: true },
        { key: "preferred_date", label: "Preferred Date", type: "date", required: true },
        { key: "message", label: "Additional Notes", type: "textarea", placeholder: "Any specific requirements?" },
      ]
    },
    brochure: {
      title: "Download Brochure",
      leadType: "brochure",
      fields: [
        { key: "name", label: "Full Name", placeholder: "Your full name", required: true },
        { key: "phone", label: "Phone", placeholder: "+91 XXXXX XXXXX", required: true },
        { key: "email", label: "Email", type: "email", placeholder: "your@email.com", required: true },
        { key: "property_type", label: "Interested In", type: "select", options: ["Luxury Apartments", "Villas", "Commercial", "Plots", "All Properties"] },
      ]
    },
  };

  return (
    <div className="bg-[#080808] min-h-screen pt-24">
      {/* Header */}
      <div className="py-16 border-b border-white/5"
        style={{ background: "linear-gradient(180deg, #0a0900 0%, #080808 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label">
            Contact Us
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-5xl font-light text-[#FAF8F3]">
            Let's Start Your<br />
            <span className="text-gold-gradient italic">Wealth Journey</span>
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact info */}
          <div>
            <img src={LOGO} alt="Sneha Reality" className="h-16 w-auto object-contain mb-6" />
            <p className="text-[#A0A0A0] text-sm leading-relaxed mb-8">
              We don't sell property. We engineer wealth. Our expert advisors are ready to guide you through India's most promising real estate market.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: MapPin, label: "Office", value: "Kharghar, Navi Mumbai, Maharashtra 410210" },
                { icon: Phone, label: "Phone", value: "+91 7977009190", href: "tel:+917977009190" },
                { icon: Mail, label: "Email", value: "info@snehareality.com", href: "mailto:info@snehareality.com" },
                { icon: Clock, label: "Hours", value: "Mon–Sat: 10 AM – 7 PM" },
              ].map((c) => (
                <div key={c.label} className="flex gap-4">
                  <div className="w-8 h-8 border border-[#D4AF37]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <c.icon size={13} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#D4AF37] tracking-wider uppercase mb-0.5">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="text-sm text-[#A0A0A0] hover:text-[#FAF8F3] transition-colors">{c.value}</a>
                    ) : (
                      <p className="text-sm text-[#A0A0A0]">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <a href="https://wa.me/917977009190?text=Hi%2C%20I%20want%20to%20enquire%20about%20properties%20in%20Navi%20Mumbai."
              target="_blank" rel="noopener noreferrer"
              className="btn-gold-solid btn-gold w-full justify-center text-xs py-3"
            >
              <MessageCircle size={13} className="mr-2" /> WhatsApp Us Now
            </a>
          </div>

          {/* Forms */}
          <div className="lg:col-span-2">
            {/* Form tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {Object.entries(formConfigs).map(([key, cfg]) => (
                <button key={key} onClick={() => setActiveForm(key)}
                  className={`text-xs px-5 py-2 border tracking-wider uppercase transition-all ${
                    activeForm === key ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-[#666]"
                  }`}
                >{cfg.title}</button>
              ))}
            </div>

            <ContactForm {...formConfigs[activeForm]} key={activeForm} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
